import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CloudUpload,
  FileDownload,
  Refresh,
  FilterList,
  DateRange,
  Close,
  PictureAsPdf,
  TableChart,
  TrendingUp,
  TrendingDown,
  Hotel,
  AttachMoney,
  Assessment,
  Info,
  People
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useDashboardData, useDateFilter, useExportData } from './hooks/useDashboardData';

const DashboardAvanzado = () => {
  // Obtener hotelId de hotelData (igual que ReserveRoom)
  const user = JSON.parse(localStorage.getItem('hotelData'));
  const hotelId = user?._id;

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Usar hooks personalizados
  // NOTA: useDateFilter ahora inicializa con "yesterday" por defecto
  const { startDate, endDate, preset, setStartDate, setEndDate, applyPreset } = useDateFilter();

  const { data, loading, error, refetch } = useDashboardData(startDate, endDate);

  const { exportToCSV, exportToJSON } = useExportData();

  // Presets de fecha disponibles
  const datePresets = [
    { value: 'today', label: 'Hoy' },
    { value: 'yesterday', label: 'Ayer' },
    { value: 'thisWeek', label: 'Esta Semana' },
    { value: 'thisMonth', label: 'Este Mes' },
    { value: 'thisYear', label: 'Este Año' },
    { value: 'custom', label: 'Personalizado' }
  ];

  const handleExportMetrics = (format) => {
    if (!data) return;

    const exportData = [
      {
        metrica: 'Ocupación',
        valor: data.metrics.occupancy.value,
        cambio: data.metrics.occupancy.change,
        periodo_anterior: data.metrics.occupancy.prevValue
      },
      {
        metrica: 'RevPar',
        valor: data.metrics.revPar.value,
        cambio: data.metrics.revPar.change,
        periodo_anterior: data.metrics.revPar.prevValue
      },
      {
        metrica: 'ADR',
        valor: data.metrics.adr.value,
        cambio: data.metrics.adr.change,
        periodo_anterior: data.metrics.adr.prevValue
      },
      {
        metrica: 'GOP',
        valor: data.metrics.gop.value,
        cambio: data.metrics.gop.change,
        periodo_anterior: data.metrics.gop.prevValue
      },
      {
        metrica: 'GOPPAR',
        valor: data.metrics.goppar?.value,
        cambio: data.metrics.goppar?.change,
        periodo_anterior: data.metrics.goppar?.prevValue
      },
      {
        metrica: 'CPOR',
        valor: data.metrics.cpor?.value,
        cambio: data.metrics.cpor?.change,
        periodo_anterior: data.metrics.cpor?.prevValue
      }
    ];

    const filename = `dashboard_metrics_${startDate}_${endDate}`;

    if (format === 'csv') {
      exportToCSV(exportData, `${filename}.csv`);
    } else if (format === 'json') {
      exportToJSON(data, `${filename}.json`);
    }

    setExportDialogOpen(false);
  };

  const handlePrintDashboard = () => {
    window.print();
  };

  // Colores para los gráficos de dona
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header con controles avanzados */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  sx={{
                    textTransform: 'none',
                    color: '#666',
                    borderColor: '#ddd'
                  }}
                >
                  Logo
                </Button>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Dashboard de Métricas
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <Tooltip title="Actualizar datos">
                  <IconButton onClick={refetch} disabled={loading} sx={{ color: '#1a237e' }}>
                    <Refresh />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Filtros">
                  <IconButton onClick={() => setFilterDialogOpen(true)} sx={{ color: '#1a237e' }}>
                    <FilterList />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => setExportDialogOpen(true)}
                  disabled={!data}
                  sx={{
                    textTransform: 'none',
                    color: '#1a237e',
                    borderColor: '#1a237e'
                  }}
                >
                  Exportar
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  onClick={handlePrintDashboard}
                  disabled={!data}
                  sx={{
                    textTransform: 'none',
                    color: '#1a237e',
                    borderColor: '#1a237e'
                  }}
                >
                  Imprimir
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Botones de Filtro Rápido - Inline */}
        <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            Filtros de Fecha:{' '}
            <Box component="span" color="text.secondary" fontWeight="normal">
              {startDate} - {endDate}
            </Box>
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { value: 'yesterday', label: 'Ayer' },
              { value: 'today', label: 'Hoy' },
              { value: 'thisMonth', label: 'Este Mes' },
              { value: 'thisYear', label: 'Este Año' },
              { value: 'lastMonth', label: 'Mes Ant.' },
              { value: 'lastYear', label: 'Año Ant.' }
            ].map((p) => (
              <Button
                key={p.value}
                size="small"
                variant={preset === p.value ? 'contained' : 'outlined'}
                onClick={() => applyPreset(p.value)}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  backgroundColor: preset === p.value ? '#1a237e' : 'transparent',
                  borderColor: preset === p.value ? '#1a237e' : 'rgba(0, 0, 0, 0.12)',
                  color: preset === p.value ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: preset === p.value ? '#0d47a1' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {p.label}
              </Button>
            ))}

            <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.5 }} />

            <Button
              size="small"
              variant={preset === 'custom' ? 'contained' : 'outlined'}
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<DateRange sx={{ fontSize: '1rem !important' }} />}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                backgroundColor: preset === 'custom' ? '#1a237e' : 'transparent',
                borderColor: preset === 'custom' ? '#1a237e' : 'rgba(0, 0, 0, 0.12)',
                color: preset === 'custom' ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: preset === 'custom' ? '#0d47a1' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              Personalizado
            </Button>
          </Box>
        </Paper>

        {/* Loading State */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Cargando datos del dashboard...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Error al cargar datos
            </Typography>
            <Typography>{error}</Typography>
            <Button onClick={refetch} variant="outlined" sx={{ mt: 2 }}>
              Reintentar
            </Button>
          </Alert>
        )}

        {/* Dashboard Content */}
        {data && (
          <>
            {/* Métricas Principales */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* 1. Occupancy */}
              <Grid item xs={12} sm={6} md={2}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography color="text.secondary" variant="caption">
                        Ocupación (%)
                      </Typography>
                      <Tooltip title="Porcentaje de habitaciones ocupadas frente al total disponible." arrow>
                        <Info fontSize="small" color="disabled" sx={{ width: 16, height: 16, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} my={1}>
                      <Typography variant="h4" fontWeight="bold">
                        {data.metrics.occupancy.value}%
                      </Typography>
                      {data.metrics.occupancy.change >= 0 ? (
                        <TrendingUp sx={{ color: 'success.main' }} />
                      ) : (
                        <TrendingDown sx={{ color: 'error.main' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      vs prev {data.metrics.occupancy.prevValue}% ({data.metrics.occupancy.change > 0 ? '+' : ''}
                      {data.metrics.occupancy.change}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 2. RevPar */}
              <Grid item xs={12} sm={6} md={2}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography color="text.secondary" variant="caption">
                        RevPar (USD)
                      </Typography>
                      <Tooltip title="Revenue Per Available Room. Ingreso promedio por habitación disponible." arrow>
                        <Info fontSize="small" color="disabled" sx={{ width: 16, height: 16, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} my={1}>
                      <Typography variant="h4" fontWeight="bold">
                        ${data.metrics.revPar.value}
                      </Typography>
                      {data.metrics.revPar.change >= 0 ? (
                        <TrendingUp sx={{ color: 'success.main' }} />
                      ) : (
                        <TrendingDown sx={{ color: 'error.main' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      vs prev ${data.metrics.revPar.prevValue} ({data.metrics.revPar.change > 0 ? '+' : ''}
                      {data.metrics.revPar.change}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 3. ADR */}
              <Grid item xs={12} sm={6} md={2}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography color="text.secondary" variant="caption">
                        ADR (USD)
                      </Typography>
                      <Tooltip title="Average Daily Rate. Tarifa promedio diaria por habitación vendida." arrow>
                        <Info fontSize="small" color="disabled" sx={{ width: 16, height: 16, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} my={1}>
                      <Typography variant="h4" fontWeight="bold">
                        ${data.metrics.adr.value}
                      </Typography>
                      {data.metrics.adr.change >= 0 ? (
                        <TrendingUp sx={{ color: 'success.main' }} />
                      ) : (
                        <TrendingDown sx={{ color: 'error.main' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      vs prev ${data.metrics.adr.prevValue} ({data.metrics.adr.change > 0 ? '+' : ''}
                      {data.metrics.adr.change}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 4. GOP */}
              <Grid item xs={12} sm={6} md={2}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography color="text.secondary" variant="caption">
                        GOP (USD)
                      </Typography>
                      <Tooltip title="Gross Operating Profit. Beneficio bruto operativo (Ingresos - Gastos)." arrow>
                        <Info fontSize="small" color="disabled" sx={{ width: 16, height: 16, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} my={1}>
                      <Typography variant="h4" fontWeight="bold">
                        ${data.metrics.gop.value}
                      </Typography>
                      {data.metrics.gop.change >= 0 ? (
                        <TrendingUp sx={{ color: 'success.main' }} />
                      ) : (
                        <TrendingDown sx={{ color: 'error.main' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      vs prev ${data.metrics.gop.prevValue} ({data.metrics.gop.change > 0 ? '+' : ''}
                      {data.metrics.gop.change}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 5. GOPPAR */}
              <Grid item xs={12} sm={6} md={2}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography color="text.secondary" variant="caption">
                        GOPPAR (USD)
                      </Typography>
                      <Tooltip title="Gross Operating Profit Per Available Room. Beneficio operativo por habitación disponible." arrow>
                        <Info fontSize="small" color="disabled" sx={{ width: 16, height: 16, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} my={1}>
                      <Typography variant="h4" fontWeight="bold">
                        ${data.metrics.goppar?.value || '0.00'}
                      </Typography>
                      {(data.metrics.goppar?.change || 0) >= 0 ? (
                        <TrendingUp sx={{ color: 'success.main' }} />
                      ) : (
                        <TrendingDown sx={{ color: 'error.main' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      vs prev ${data.metrics.goppar?.prevValue || '0.00'} ({data.metrics.goppar?.change > 0 ? '+' : ''}
                      {data.metrics.goppar?.change}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 6. CPOR (Nuevo) */}
              <Grid item xs={12} sm={6} md={2}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography color="text.secondary" variant="caption">
                        CPOR (USD)
                      </Typography>
                      <Tooltip title="Cost Per Occupied Room. Costo promedio por habitación ocupada." arrow>
                        <Info fontSize="small" color="disabled" sx={{ width: 16, height: 16, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Box display="flex" alignItems="baseline" gap={0.5} my={1}>
                      <Typography variant="h4" fontWeight="bold">
                        ${data.metrics.cpor?.value || '0.00'}
                      </Typography>
                      {(data.metrics.cpor?.change || 0) > 0 ? (
                        <TrendingUp sx={{ color: 'error.main' }} />
                      ) : (
                        <TrendingDown sx={{ color: 'success.main' }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      vs prev ${data.metrics.cpor?.prevValue || '0.00'} ({data.metrics.cpor?.change > 0 ? '+' : ''}
                      {data.metrics.cpor?.change}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* NEW LAYOUT: Metrics Left, Charts Right */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* LEFT COLUMN: Key Metrics Grid */}
              <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                  {/* Row 1: Rooms Sold & Revenue */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Hotel color="primary" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            Habitaciones Vendidas
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold">
                          {data.roomsSold.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          vs prev {data.roomsSold.prevValue} ({data.roomsSold.change > 0 ? '+' : ''}
                          {data.roomsSold.change}%)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <AttachMoney color="success" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            Ingresos del Periodo
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold">
                          ${data.periodRevenue.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          vs prev ${data.periodRevenue.prevValue} ({data.periodRevenue.change > 0 ? '+' : ''}
                          {data.periodRevenue.change}%)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Row 2: Stay Type & Price Type */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Hotel color="secondary" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            Estancia Más Popular
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold">
                          {data.stayTypeData?.[0]?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {data.stayTypeData?.[0]?.value || 0} reservas ({data.stayTypeData?.[0]?.percentage || 0}%)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <People color="warning" />
                          <Typography variant="subtitle2" fontWeight="bold">
                            Huéspedes Recurrentes
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold">
                          {data.recurringGuests?.value || 0}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {data.recurringGuests?.recurring || 0} de {data.recurringGuests?.totalUnique || 0} clientes únicos
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT COLUMN: Payment & Currency Charts */}
              <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                  {/* Payment Method Chart */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom textAlign="center">
                          Métodos de Pago
                        </Typography>
                        <Box sx={{ height: 220, position: 'relative' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={data.paymentMethodData}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={60}
                                paddingAngle={2}
                                dataKey="value"
                                stroke="none"
                                label={({ percentage }) => `${percentage}%`}
                              >
                                {data.paymentMethodData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <RechartsTooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Currency Chart */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom textAlign="center">
                          Distribución de Monedas
                        </Typography>
                        <Box sx={{ height: 220, position: 'relative' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={data.currencyData}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={60}
                                paddingAngle={2}
                                dataKey="value"
                                stroke="none"
                                label={({ percentage }) => `${percentage}%`}
                              >
                                {data.currencyData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <RechartsTooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Revenue Trend Chart */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                Tendencia de Ingresos
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.revenueTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </>
        )}

        {/* Dialog de filtros */}
        <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Filtros del Dashboard
              </Typography>
              <IconButton onClick={() => setFilterDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Selección Rápida
                </Typography>
                <Grid container spacing={1}>
                  {[
                    { value: 'today', label: 'Hoy' },
                    { value: 'yesterday', label: 'Ayer' },
                    { value: 'thisWeek', label: 'Esta Semana' },
                    { value: 'thisMonth', label: 'Este Mes' },
                    { value: 'thisYear', label: 'Este Año' }
                  ].map((p) => (
                    <Grid item xs={6} sm={4} key={p.value}>
                      <Button
                        fullWidth
                        variant={preset === p.value ? 'contained' : 'outlined'}
                        onClick={() => applyPreset(p.value)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          py: 1,
                          backgroundColor: preset === p.value ? '#1a237e' : 'transparent',
                          borderColor: preset === p.value ? '#1a237e' : 'rgba(0, 0, 0, 0.12)',
                          color: preset === p.value ? 'white' : 'text.primary',
                          '&:hover': {
                            backgroundColor: preset === p.value ? '#0d47a1' : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        {p.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Rango personalizado
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha inicio"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fecha fin"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={() => {
                refetch();
                setFilterDialogOpen(false);
              }}
              sx={{
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1'
                }
              }}
            >
              Aplicar filtros
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de exportación */}
        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Exportar Dashboard
              </Typography>
              <IconButton onClick={() => setExportDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
              Selecciona el formato en el que deseas exportar los datos del dashboard
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<TableChart />}
                  onClick={() => handleExportMetrics('csv')}
                  sx={{
                    py: 2,
                    justifyContent: 'flex-start',
                    textTransform: 'none'
                  }}
                >
                  <Box sx={{ textAlign: 'left', ml: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Exportar a CSV
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Formato de tabla para Excel
                    </Typography>
                  </Box>
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => handleExportMetrics('json')}
                  sx={{
                    py: 2,
                    justifyContent: 'flex-start',
                    textTransform: 'none'
                  }}
                >
                  <Box sx={{ textAlign: 'left', ml: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Exportar a JSON
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Formato de datos estructurados
                    </Typography>
                  </Box>
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  onClick={handlePrintDashboard}
                  sx={{
                    py: 2,
                    justifyContent: 'flex-start',
                    textTransform: 'none'
                  }}
                >
                  <Box sx={{ textAlign: 'left', ml: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Imprimir / PDF
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Vista de impresión del dashboard
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* Estilos para impresión */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #dashboard-print-area, #dashboard-print-area * {
              visibility: visible;
            }
            #dashboard-print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            button, .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default DashboardAvanzado;
